import axios from 'axios';
import { apiDomain } from '@/config';
import i18n from '@/i18n';
import * as types from './types';

const actions = {
  resetState({ state, commit, dispatch }) {
    commit(types.RESET_STATE);
    if (state.authUser.is_employee) {
      dispatch('employee/reset', null, { root: true });
    } else {
      dispatch('company/reset', null, { root: true });
    }
    dispatch('user/hideNavigationDrawer', null, { root: true });
  },
  setUserObject: ({ commit }, userObj) => {
    commit(types.SET_AUTH_USER, userObj);
  },
  setInRegistrationProcess: ({ commit }, inRegistrationProcess) => {
    commit(types.SET_IN_REGISTRATION_PROCESS, inRegistrationProcess);
  },
  setLanguage: ({ commit, dispatch }, language) => {
    commit(types.SET_LANGUAGE, language);
    dispatch('general/setCountries', language, { root: true });
  },
  setProfileImage: ({ commit }, args) => {
    commit(types.SET_PROFILE_IMAGE, args);
  },
  setPredefinedSeason: ({ commit }, args) => {
    commit(types.SET_PREDEFINED_SEASON, args);
  },
  showNavigationDrawer: ({ commit }) => {
    commit(types.SHOW_NAVIGATION_DRAWER);
  },
  hideNavigationDrawer: ({ commit }) => {
    commit(types.HIDE_NAVIGATION_DRAWER);
  },
  toggleNavigationDrawer: ({ commit }) => {
    commit(types.TOGGLE_NAVIGATION_DRAWER);
  },
  async fetchUserProfile({ commit }) {
    commit(types.FETCH_USER_PROFILE_PENDING);
    try {
      const response = await axios.get(`${apiDomain}auth/user`);
      commit(types.FETCH_USER_PROFILE_FULFILLED, response.data.data.user);
      return response.data.data.user;
    } catch (error) {
      commit(types.FETCH_USER_PROFILE_ERROR);
      return Promise.reject(error);
    }
  },
  async updateUserProfile({ commit, dispatch }, form) {
    commit(types.UPDATE_USER_PROFILE_PENDING);
    try {
      const url = form.is_employee ? `${apiDomain}employee` : `${apiDomain}user`;
      const response = await axios.post(url, form);
      await dispatch('fetchUserProfile');
      dispatch('setLanguage', form.language);
      commit(types.UPDATE_USER_PROFILE_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.updateUserProfileSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.UPDATE_USER_PROFILE_ERROR);
      dispatch('messages/showMessage', {
        text: `${i18n.t('texts.alert.updateUserProfileError')}. ${error}`,
        color: 'error',
      }, { root: true });
      return Promise.reject(error);
    }
  },
  async sendPasswordResetMail({ commit, dispatch }, form) {
    commit(types.SEND_PASSWORD_RESET_MAIL_PENDING);
    try {
      const response = await axios.post(`${apiDomain}password/email`, form);
      commit(types.SEND_PASSWORD_RESET_MAIL_FULFILLED, response);
      // TODO Prefix message key with "alert."
      dispatch('messages/showMessage', { key: 'passwordResetInfo', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      const errors = Object.keys(error.response.data.errors)[0];
      const errorText = error.response.data.errors[errors][0];
      dispatch('messages/showMessage', { text: errorText, color: 'error' }, { root: true });
      commit(types.SEND_PASSWORD_RESET_MAIL_ERROR);
      return Promise.reject(error);
    }
  },
  async sendPasswordReset({ commit, dispatch }, form) {
    commit(types.SEND_PASSWORD_RESET_PENDING);
    try {
      const response = await axios.post(`${apiDomain}password/reset`, form);
      commit(types.SEND_PASSWORD_RESET_FULFILLED, response);
      // TODO Prefix message key with "alert."
      dispatch('messages/showMessage', { key: 'passwordResetSuccessful', color: 'success' }, { root: true });
      return Promise.resolve(response);
    } catch (error) {
      commit(types.SEND_PASSWORD_RESET_ERROR);
      // TODO what about that?
      // Object.keys(error.response.data).forEach(index => `${error.response.data[index][0]} <br>`);
      // this.errors = error.response.data;
      dispatch('messages/showMessage', { key: 'passwordResetFailed', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async fetchFavorites({ commit }) {
    commit(types.FETCH_FAVORITES_PENDING);
    try {
      const response = await axios.get(`${apiDomain}employee/favorites/jobs`);
      commit(types.FETCH_FAVORITES_FULFILLED, response.data.data);
      return true;
    } catch (error) {
      commit(types.FETCH_FAVORITES_ERROR);
      return Promise.reject(error);
    }
  },
  async addFavorite({ commit, dispatch }, payload) {
    commit(types.ADD_FAVORITE_PENDING);
    try {
      await axios.post(`${apiDomain}employee/favorites/jobs/${payload.id}`);
      commit(types.ADD_FAVORITE_FULFILLED, payload);
      dispatch('messages/showMessage', { key: 'alert.addFavoriteSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.ADD_FAVORITE_ERROR);
      dispatch('messages/showMessage', { key: 'alert.addFavoriteError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async removeFavorite({ commit, dispatch }, payload) {
    commit(types.REMOVE_FAVORITE_PENDING);
    try {
      await axios.delete(`${apiDomain}employee/favorites/jobs/${payload.id}`);
      commit(types.REMOVE_FAVORITE_FULFILLED, payload);
      dispatch('messages/showMessage', { key: 'alert.removeFavoriteSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.REMOVE_FAVORITE_ERROR);
      dispatch('messages/showMessage', { key: 'alert.removeFavoriteError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async fetchNotifications({ state, commit, dispatch }, id) {
    commit(types.FETCH_NOTIFICATIONS_PENDING);
    try {
      const url = state.authUser.is_employee
        ? `${apiDomain}notifications` : `${apiDomain}company/${id}/notifications`;
      commit(types.FETCH_NOTIFICATIONS_FULFILLED, await axios.get(url));
      dispatch('refetchNotifications', id);
      return true;
    } catch (error) {
      commit(types.FETCH_NOTIFICATIONS_ERROR);
      return Promise.reject(error);
    }
  },
  refetchNotifications: ({ state, dispatch }, id) => {
    setTimeout(() => {
      if (state.authUser.loggedIn) {
        dispatch('fetchNotifications', id);
        dispatch('company/fetchCompanies', null, { root: true });
      }
    }, 120000);
  },
  async updateNotification({
    rootState, state, commit, dispatch,
  }, payload) {
    commit(types.UPDATE_NOTIFICATION_PENDING);
    try {
      const method = state.authUser.is_employee ? 'PATCH' : 'POST';
      const url = `${apiDomain}notifications/${payload}`;
      const response = await axios.patch(url, method);
      const companyId = state.authUser.is_employee ? null : rootState.company.profile.company.id;
      commit(types.UPDATE_NOTIFICATION_SUCCESFUL, response);
      dispatch('fetchNotifications', companyId);
      dispatch('company/fetchCompanies', null, {
        root: true,
      });
    } catch (error) {
      commit(types.UPDATE_NOTIFICATION_ERROR);
    }
  },
  async updateAllNotificationsRead({
    rootState, state, commit, dispatch,
  }) {
    commit(types.UPDATE_ALL_NOTIFICATIONS_READ_PENDING);
    try {
      const url = state.authUser.is_employee
        ? `${apiDomain}notifications`
        : `${apiDomain}company/${rootState.company.profile.company.id}/notifications`;
      const response = await axios.patch(url);
      commit(types.UPDATE_ALL_NOTIFICATIONS_READ_FULFILLED, response);
      dispatch('fetchNotifications', rootState.company.profile.company.id);
      dispatch('company/fetchCompanies', null, {
        root: true,
      });
      dispatch('messages/showMessage', { key: 'alert.updateAllNotificationsSuccessful', color: 'success' }, { root: true });
    } catch (error) {
      commit(types.UPDATE_ALL_NOTIFICATIONS_READ_ERROR);
      dispatch('messages/showMessage', { key: 'alert.updateAllNotificationsError', color: 'error' }, { root: true });
    }
  },
  async sendContactForm({ commit, dispatch }, form) {
    commit(types.SEND_CONTACT_FORM_PENDING);
    try {
      const response = await axios.post(`${apiDomain}contact`, form);
      commit(types.SEND_CONTACT_FORM_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.sendContactSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.SEND_CONTACT_FORM_ERROR);
      dispatch('messages/showMessage', { key: 'alert.sendContactError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async registerNewsletter({ commit, dispatch }, form) {
    commit(types.REGISTER_NEWSLETTER_PENDING);
    try {
      const response = await axios.post(`${apiDomain}newsletter`, form);
      commit(types.REGISTER_NEWSLETTER_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.registerNewsletterSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.REGISTER_NEWSLETTER_ERROR);
      dispatch('messages/showMessage', { key: 'alert.registerNewsletterError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
};

export default actions;
